import { defineBoot } from '#q-app'
import { Capacitor } from '@capacitor/core'
import { StorageAdapter } from 'src/offline/storage/StorageAdapter'
import { isFlagEnabled, setOrgResolver } from 'src/offline/flags'
import { refreshRemoteOfflineFlag } from 'src/offline/remote-flag'
import { recoverInFlight } from 'src/offline/repositories/recovery'
import { migrateV1Answers } from 'src/offline/storage/migrate-v1-answers'
import { startSyncWorker, isSyncWorkerStarted } from 'src/offline/sync/sync-worker'
import { sweepCoveredDeadAnswerOps } from 'src/offline/sync/sweep-covered-dead-answers'
import { registerAllCatalogs } from 'src/offline/sync/pull/catalogs'
import { setScopeResolver, getScope, revalidateStale, bootstrapTierB, pullCatalog } from 'src/offline/sync/pull/pull-engine'
import { syncNow } from 'src/offline/sync/sync-worker'
import * as domainOccurrences from 'src/offline/domain/occurrences'
import * as domainTasks from 'src/offline/domain/tasks'
import { useUsersStore } from 'src/stores/index.js'
import { setOfflineUserResolver } from 'src/offline/session'
import { isNativePlatform } from 'src/offline/storage/platform'
import { captureOfflineError } from '@/offline/telemetry'
import * as dbgLog from 'src/offline/logger'
const { dbg, dbgError } = dbgLog

/**
 * Snapshot de salud de la capa offline en UNA llamada — clave para diagnosticar en device:
 * dice si arrancó nativo o cayó a web, qué backend abrió, si los plugins SQLite/filesystem están,
 * y si el flag/debug están activos. En device: window.__offlineV2Debug.status().
 */
export function offlineStatus () {
  // isPluginAvailable() da true aunque solo exista la implementación WEB del plugin (fallback);
  // gateado por isNativePlatform() para que estos campos reflejen de verdad el backend nativo.
  const has = (name) => { try { return isNativePlatform() && Capacitor.isPluginAvailable(name) } catch { return false } }
  return {
    platform: isNativePlatform() ? 'native' : 'web',
    backend: StorageAdapter._backend ? StorageAdapter._backend.name : null,
    dbOpen: !!StorageAdapter._backend,
    sqlitePlugin: has('CapacitorSQLite'),
    filesystem: has('Filesystem'),
    debugEnabled: dbgLog.isDebugEnabled(),
    offlineFlag: isFlagEnabled('offline_mode_enabled'),
    logEntries: dbgLog.getLog().length
  }
}

/**
 * Expone el API de debug en window SIEMPRE (incl. APK release) para diagnosticar en device.
 * Namespace `__offlineV2*` (coincide con el nombre persistido de la BD, binnacle_offline_v2).
 */
function exposeDebugApi () {
  try {
    if (typeof window === 'undefined') return
    window.__offlineV2Debug = {
      dumpLog: dbgLog.dumpLog,
      dumpLogText: dbgLog.dumpLogText,
      copyLog: dbgLog.copyLog,
      getLog: dbgLog.getLog,
      clearLog: dbgLog.clearLog,
      enableDebug: dbgLog.enableDebug,
      disableDebug: dbgLog.disableDebug,
      isDebugEnabled: dbgLog.isDebugEnabled,
      status: offlineStatus
    }
  } catch { /* noop */ }
}

/**
 * Boot de la capa offline (SQLite nativo @capacitor-community/sqlite / OPFS-sahpool|wa-sqlite-idb en web).
 *
 * Arranca siempre: Formularios trabaja sobre la base local sin importar `offline_mode_enabled`
 * (esa bandera solo decide si Tareas captura offline). StorageAdapter.open() selecciona el backend (en web por capability detection:
 * opfs-sahpool -> wa-sqlite IDBBatchAtomic) y corre la migración user_version (misma escalera web y
 * nativo); en nativo aplica los PRAGMAs de durabilidad dentro del open del backend.
 *
 * Este boot NO importa ningún motor (sqlite-wasm/wa-sqlite/@capacitor-community/sqlite): la
 * inicialización del motor vive DENTRO del backend que elige el StorageAdapter.
 */
// Arranque idempotente de la capa offline, reutilizable [review Hugo #1 blocker]. Se llama en DOS
// momentos: (1) el cold boot de abajo; (2) TRAS EL LOGIN (auth/actions.js), donde es no-op porque el
// cold boot ya corrió (lo que ese camino aporta es re-arrancar el worker y reanudar el drain tras un
// logout). El promise-guard evita doble arranque y hace las llamadas concurrentes/repetidas no-op.
/**
 * Health-check NO DESTRUCTIVO de la conexion, justo despues de open(). Es SOLO LECTURA: `SELECT 1`
 * no crea tablas, no escribe y no borra nada.
 *
 * Por que hace falta: open() puede resolver sin lanzar y aun asi dejar una conexion que no responde
 * (caso real en Android: el plugin nativo pierde su llave interna en el keystore del sistema). Con
 * open() "OK" pero la base muda, quien revienta es la PRIMERA escritura real de una ocurrencia, ya
 * con el usuario adentro y sin rastro del origen. Esta lectura trivial adelanta el diagnostico al boot.
 *
 * Marca la fase en el error para que el catch de _bootOfflineLayer la reporte aparte en Sentry.
 * NO repara nada: el unico camino de recuperacion sigue siendo el self-heal de foreground
 * (installLifecycleHandlers -> reopenIfClosed). La base NUNCA se recrea ni se borra: dentro viven
 * fotos capturadas que todavia no se han subido al servidor.
 */
async function _assertDbResponds () {
  try {
    await StorageAdapter.query('SELECT 1')
  } catch (error) {
    try { error._offlineBootPhase = 'boot.healthcheck' } catch { /* error no extensible: se reporta con la fase default */ }
    throw error
  }
}

/**
 * Reporta un fallo del boot a Sentry con SU PROPIA fase. Antes todo caia bajo `boot.open`, asi que
 * en el dashboard era imposible separar "el plugin nativo perdio su llave del keystore" de "fallo la
 * migracion v1": mismo evento, causas y gravedad opuestas.
 *
 * El snapshot de offlineStatus() dice si el plugin nativo esta presente y si la BD quedo abierta.
 * Todo va envuelto: la telemetria NUNCA puede tumbar el arranque de la app. Solo observacion:
 * no repara, no reintenta y no toca la base.
 */
function _reportBootError (error, phase) {
  try {
    let status = null
    try { status = offlineStatus() } catch { /* snapshot best-effort: si falla se reporta sin el */ }
    captureOfflineError(error, { phase: error?._offlineBootPhase || phase, status })
  } catch { /* noop */ }
}

/**
 * Ejecuta UN paso del boot AISLADO: si lanza, lo loguea, lo reporta con su fase y devuelve false,
 * pero nunca propaga.
 *
 * Por que existe: la secuencia post-open vivia bajo un unico `try` gigante, asi que cualquier fallo
 * intermedio (catalogos, recoverInFlight, migracion v1) cancelaba TODO lo que venia detras. Lo mas
 * caro de perder era startSyncWorker(): sin worker el outbox no drena en toda la sesion, o sea que
 * respuestas y FOTOS ya capturadas se quedan sin subir hasta el siguiente cold start exitoso.
 * Aislando por paso, un fallo no dependiente deja de costar el drenado de la sesion.
 */
async function _runStep (phase, run) {
  try {
    await run()
    return true
  } catch (error) {
    dbgError('boot', `paso "${phase}" fallo (se continua con los pasos no dependientes)`, error)
    _reportBootError(error, phase)
    return false
  }
}

// Cota dura para los dos PASOS BLOQUEANTES del boot (ver _bootOfflineLayer). Sin esto, un backend
// colgado (p.ej. wa-sqlite-idb contendiendo la misma IndexedDB compartida entre 4+ pestañas —
// bug real: la 4a/5a/6a pestaña se queda en blanco para siempre) deja el boot file sin resolver,
// y Quasar NUNCA llama app.mount() -> pantalla en blanco permanente, sin error en consola. 20s:
// generoso para la instanciacion de WASM en un dispositivo lento, corto para que la pestaña se
// vuelva usable en vez de quedar colgada. El open() real sigue corriendo de fondo (las promesas de
// JS no son cancelables); si eventualmente resuelve, this._backend igual queda publicado y el
// self-heal de foreground se beneficia.
const BOOT_STEP_TIMEOUT_MS = 20000

function withTimeout (promise, ms, label) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`[offline-v2] boot step "${label}" timeout tras ${ms}ms`))
    }, ms)
    promise.then(
      (value) => { clearTimeout(timer); resolve(value) },
      (error) => { clearTimeout(timer); reject(error) }
    )
  })
}

let _offlineBootPromise = null
export async function ensureOfflineBooted () {
  if (_offlineBootPromise) return _offlineBootPromise
  _offlineBootPromise = _bootOfflineLayer()
  return _offlineBootPromise
}

async function _bootOfflineLayer () {
  // Expone el debug API ANTES del open para poder recuperar el log aunque el boot
  // falle. En device: window.__offlineV2Debug.dumpLog() / .status().
  exposeDebugApi()
  dbg('boot', `arranque offline (plataforma=${isNativePlatform() ? 'nativo' : 'web'})`)
  try {
    // SELF-HEAL primero (boot-race fix): instala los handlers de lifecycle ANTES del open(). Si open()
    // lanzara (p.ej. plugin nativo aún no listo), el paso solo loguea y reporta; con los handlers YA
    // instalados, el próximo foreground (resume/visibilitychange ->
    // reopenIfClosed -> open) reabre Y migra la DB (this._backend solo se publica tras éxito), así las
    // CAPTURAS de occurrences se recuperan enteras -> cero pérdida. Ese self-heal es el UNICO camino de
    // recuperacion: aqui no se recrea, borra ni reinicializa nada (dentro de la BD viven fotos ya
    // capturadas que todavia no se subieron). Si el boot se corta en open/healthcheck, _completeBootWhenOpened
    // corre los pasos que faltan cuando la base se recupera a mitad de sesion.
    // installLifecycleHandlers es idempotente y solo registra listeners (no depende de la conexión).
    await _runStep('boot.lifecycle', () => StorageAdapter.installLifecycleHandlers())
    dbg('boot', 'lifecycle handlers instalados (antes de open: self-heal del boot-race)')

    // Dueño y alcance: no tocan la base, asi que van antes de los pasos que pueden cortar el boot.
    // SCOPE RESOLVER real, cableado ANTES de que el worker dispare su primer tick de pull: inyecta el
    // lector del usuario autenticado para que el pull Tier B y el down-sync Tier A resuelvan org/user en
    // runtime (sin esto getScope devuelve undefined y todo descriptor needsScope se salta). useUsersStore
    // es un store Pinia; import estático OK aquí (el boot ya corre con Quasar/Pinia inicializados).
    await _runStep('boot.scopeResolver', () => setScopeResolver(() => {
      const u = useUsersStore().current
      if (!u) return undefined
      const organizationId = (u.organization && u.organization.id) || u.franchise_organization_id || u.organization_id
      return organizationId ? { organizationId, userId: u.id } : undefined
    }))

    // Dueño de las ops del outbox: sella cada op con el user_id actual y filtra el drain por usuario
    // (device compartido no pushea lo de otro usuario). Lazy: lee el usuario autenticado en runtime.
    // Va en SU PROPIO paso, aparte del scope resolver: si aquel fallara, este igual queda cableado antes
    // del primer tick, que es lo que el drain necesita de verdad.
    await _runStep('boot.userResolver', () => setOfflineUserResolver(() => useUsersStore().current?.id))

    // ---- PASOS BLOQUEANTES: los unicos dos que justifican abortar la secuencia ----
    // open(): sin conexión publicada no hay lectura ni escritura posible, y el worker no tendria de
    // donde drenar. Abortar aqui es la decision correcta y EXPLICITA (no un efecto colateral).
    if (!(await _runStep('boot.open', () => withTimeout(StorageAdapter.open(), BOOT_STEP_TIMEOUT_MS, 'boot.open')))) {
      _completeBootWhenOpened({ checkNow: true })
      return
    }
    dbg('boot', 'StorageAdapter.open() OK', offlineStatus())

    // Confirma que la conexion RESPONDE, no solo que open() no lanzo. Lectura trivial, sin escrituras.
    // DECISION EXPLICITA de abortar: si `SELECT 1` falla, la base esta muda y el worker no podria leer
    // el outbox; arrancarlo solo generaria un tick que revienta cada intervalo y ruido en Sentry, sin
    // drenar nada. Se reporta y se sale; cuando el self-heal de foreground reabre la base,
    // _completeBootWhenOpened repite el health-check y corre los pasos tolerantes.
    if (!(await _runStep('boot.healthcheck', () => withTimeout(_assertDbResponds(), BOOT_STEP_TIMEOUT_MS, 'boot.healthcheck')))) {
      _completeBootWhenOpened({ checkNow: false })
      return
    }
    dbg('boot', 'health-check de lectura OK (la conexion responde)')

    await _runPostOpenSteps()
  } catch (error) {
    // Red de seguridad: con cada paso ya aislado, aqui solo puede caer algo del propio orquestador
    // (un dbg que reviente, etc.). El boot NUNCA debe tumbar el arranque de la app, asi que se contiene
    // y se reporta con fase propia para no contaminar `boot.open` en Sentry. El self-heal (handlers ya
    // instalados) reintenta open() en el próximo foreground.
    dbgError('boot', 'boot error inesperado (se reintentará al volver a foreground via reopenIfClosed)', error)
    _reportBootError(error, 'boot.unexpected')
  }
}

// Si el boot se corto en open/healthcheck, completa los pasos que faltan la primera vez que la base abre
// (open tardio tras el timeout, o reopenIfClosed al volver a primer plano) y responde al health-check.
function _completeBootWhenOpened ({ checkNow }) {
  let running = false
  const complete = async () => {
    if (running) return
    running = true
    try {
      if (!(await _runStep('boot.healthcheck', () => withTimeout(_assertDbResponds(), BOOT_STEP_TIMEOUT_MS, 'boot.healthcheck')))) return
      stop()
      dbg('boot', 'base recuperada a mitad de sesion -> se completan los pasos del boot')
      await _runPostOpenSteps()
    } finally {
      running = false
    }
  }
  const stop = StorageAdapter.onOpened(complete)
  if (checkNow && StorageAdapter.isReady()) complete()
}

async function _runPostOpenSteps () {
  try {
    // ---- PASOS TOLERANTES: cada uno aislado, un fallo NO cancela los siguientes ----
    // Solicita almacenamiento persistente UNA vez y loguea el booleano concedido/denegado. No es
    // garantía (el servidor es la red de cero-pérdida); delega en StorageAdapter. No-op en nativo.
    // Nada depende de esto: si falla, el resto sigue igual.
    await _runStep('boot.persistence', () => StorageAdapter.requestPersistence())

    // Registrar los descriptores de catálogos Tier B: llena el registry (idempotente). Debe quedar ANTES
    // del primer tick de pull del worker. Si falla, solo se pierde el PULL de catalogos de esta sesion:
    // el push del outbox (respuestas y fotos) no lo necesita, asi que jamas debe frenar al worker.
    await _runStep('boot.catalogs', () => registerAllCatalogs())

    // Rescatar filas in_flight obsoletas (in_flight -> pending) ANTES de arrancar el worker, para que un
    // crash previo deje la cola completamente re-drenable. recoverInFlight es una sentencia idempotente.
    // Si falla, esas filas siguen en in_flight (se recuperan en el proximo arranque), pero el resto del
    // outbox ya esta en pending y drena igual: no es motivo para no arrancar el worker. Si el worker ya
    // arranco (login tras un boot que no abrio la base), sus filas in_flight pueden ser envios vivos: no se tocan.
    if (!isSyncWorkerStarted()) await _runStep('boot.recoverInFlight', () => recoverInFlight())

    // Migrador ÚNICA-VEZ de las answers/addons pendientes del almacén legacy (answers.json) hacia la capa
    // offline. Corre tras open (la BD ya existe) y ANTES de startSyncWorker (las re-encola al outbox para
    // que el primer drain las incluya). Best-effort + idempotente por marca meta: nunca bloquea el boot,
    // y su fallo no puede costar el drenado de lo que YA esta en el outbox.
    await _runStep('boot.migrateV1', () => migrateV1Answers())

    // Barrido única-vez (TKT-2499): colapsa a done los ops de respuesta en dead que YA quedaron cubiertos
    // por un sync posterior antes de este deploy; sin esto, una auditoría finalizada con un dead viejo
    // bloquearía el correo/PDF para siempre. ANTES de startSyncWorker para correr antes de que el tick
    // pode el done que prueba la cobertura. Idempotente y marcado en meta; un fallo no cancela el boot.
    await _runStep('boot.sweepCoveredDeads', () => sweepCoveredDeadAnswerOps())

    // Arranca los disparadores del drain (online/focus/visibilitychange/timer). Este es el paso que NO se
    // puede perder: sin worker, respuestas y fotos ya capturadas se quedan sin subir toda la sesion. Por
    // eso llega hasta aqui aunque cualquiera de los pasos tolerantes de arriba haya fallado.
    const workerStarted = await _runStep('boot.startWorker', () => startSyncWorker())
    if (workerStarted) dbg('boot', 'sync worker arrancado -> el outbox drena en esta sesion')

    // Superficie dev/test SOLO en dev: deja sondear el StorageAdapter + la fachada de dominio + syncNow
    // desde el smoke e2e / DevTools (como lo haría la UI) sin recablear nada del runtime ni exponer el
    // plugin en producción. Gated por import.meta.env.DEV (built-in de Vite).
    if (import.meta.env.DEV) {
      await _runStep('boot.devSurface', () => {
        window.__offlineV2 = StorageAdapter
        window.__offlineV2Domain = { occurrences: domainOccurrences, tasks: domainTasks }
        window.__offlineV2SyncNow = syncNow
        window.__offlineV2Pull = { getScope, revalidateStale, bootstrapTierB, pullCatalog }
      })
    }
  } catch (error) {
    // Red de seguridad: con cada paso ya aislado, aqui solo puede caer algo del propio orquestador
    // (un dbg que reviente, etc.). El boot NUNCA debe tumbar el arranque de la app, asi que se contiene
    // y se reporta con fase propia para no contaminar `boot.open` en Sentry. El self-heal (handlers ya
    // instalados) reintenta open() en el próximo foreground.
    dbgError('boot', 'boot error inesperado (se reintentará al volver a foreground via reopenIfClosed)', error)
    _reportBootError(error, 'boot.unexpected')
  }
}

export default defineBoot(async () => {
  // Inyecta el resolver de ORG: la allowlist por organización (que hoy decide solo Tareas) necesita el
  // org id del usuario autenticado, y flags.js no importa el store (evita ciclos). Debe quedar seteado
  // ANTES de cualquier isFlagEnabled.
  // Mismo extractor de org que setScopeResolver de abajo.
  const orgOfCurrentUser = () => {
    const u = useUsersStore().current
    if (!u) return undefined
    return (u.organization && u.organization.id) || u.franchise_organization_id || u.organization_id
  }
  setOrgResolver(orgOfCurrentUser)
  // KILL SWITCH REMOTO (Tareas): se refresca SIN gatear (ver remote-flag.js). Si esto se gateara por la
  // bandera, apagarla desde el back sería un billete de ida: dejaría de refrescarse y no habría forma de
  // volver a encenderla sin release. Best-effort: sin red/sin sesión conserva el último cacheado y no
  // rompe el boot.
  await refreshRemoteOfflineFlag(orgOfCurrentUser()).catch(() => {})
  await ensureOfflineBooted()
})
